/**
 * Handles filter updates by managing array field merging and duplicate detection
 * @param {Object} updateData - The filter update data with action, field, value, label, etc.
 * @param {Array} includeFilters - Current include filters
 * @param {Array} excludeFilters - Current exclude filters
 * @param {Function} setIncludeFilters - State setter for include filters
 * @param {Function} setExcludeFilters - State setter for exclude filters
 * @param {Array} boostFilters - Current boost filters (optional)
 * @param {Function} setBoostFilters - State setter for boost filters (optional)
 * @param {string} action - 'include', 'exclude', or 'boost' action from updateData
 */
export const handleFilterUpdate = (
  updateData,
  includeFilters,
  excludeFilters,
  setIncludeFilters,
  setExcludeFilters,
  boostFilters,
  setBoostFilters
) => {
  const { action, field } = updateData;
  delete updateData.action;
  
  // Keep boost field only when action is boost
  if (action !== 'boost') {
    delete updateData.boost;
  }
  
  // Determine which filter arrays to use
  let currentFilters, setFilters;
  if (action === 'include') {
    currentFilters = includeFilters;
    setFilters = setIncludeFilters;
  } else if (action === 'exclude') {
    currentFilters = excludeFilters;
    setFilters = setExcludeFilters;
  } else if (action === 'boost') {
    currentFilters = boostFilters;
    setFilters = setBoostFilters;
  }
  
  // Check if this is an array field that should be merged
  if (updateData.filter === 'terms' || updateData.filter === 'geo') {
    // Find existing filter with the same field
    const existingFilterIndex = currentFilters.findIndex(filter => {
      return (filter.filter === updateData.filter) && (filter.field === field);
    });
    
    if (existingFilterIndex !== -1) {
      // Merge with existing filter
      setFilters(prev => {
        const updated = [...prev];
        const existingFilter = updated[existingFilterIndex];
        
        // Convert existing value to array if it's not already
        const existingValues = Array.isArray(existingFilter.value) 
          ? existingFilter.value 
          : [existingFilter.value];
        
        // Convert new value to array if it's not already
        const newValues = Array.isArray(updateData.value) 
          ? updateData.value 
          : [updateData.value];
        
        // Merge values and remove duplicates
        const mergedValues = [...new Set([...existingValues, ...newValues])];
        
        // Update the existing filter with merged values
        updated[existingFilterIndex] = {
          ...existingFilter,
          value: mergedValues,
          label: `${existingFilter.label}, ${updateData.label}` // Concatenate labels
        };
        
        return updated;
      });
    } else {
      // No existing filter, add new one
      setFilters(prev => [...prev, updateData]);
    }
  } else {
    // Non-array field - check for duplicates and add normally
    const isDuplicate = currentFilters.some(existingFilter => {
      const { action: existingAction, ...existingFilterData } = existingFilter;
      const { action: newAction, ...newFilterData } = updateData;
      return JSON.stringify(existingFilterData) === JSON.stringify(newFilterData);
    });
    
    if (isDuplicate) return;
    
    setFilters(prev => [...prev, updateData]);
  }
};
